import { useOutletContext, useSearchParams } from "react-router";

import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import { EmptystateSearchNoResultsColored } from "components/icons/generated";
import { getSearchQuery } from "components/SearchInput/helpers";
import useTableSort from "components/Table/useTableSort";
import EmptyState from "ds/components/EmptyState";
import TableSimple from "ds/components/TableSimple";
import useAnalytics from "hooks/useAnalytics";
import useErrorHandle from "hooks/useErrorHandle";
import useSelfHostedEnabledCloudIntegrations from "shared/CloudIntegration/useSelfHostedEnabledCloudIntegrations";
import { SearchQueryOrderDirection } from "types/generated";

import { useLayoutActions } from "../../hooks/useLayoutActions";
import { useLayoutHeading } from "../../hooks/useLayoutHeading";
import { IntegrationView } from "../../types";
import CreateIntegrationButton from "./components/CreateIntegrationButton";
import { INTEGRATIONS_AZURE_TABLE_CELLS } from "./components/Table/Cells";
import useIntegrationsAzureTableStore from "./components/Table/tableStore";
import { AZURE_COLUMN_ID, AZURE_TABLE_FILTERS_STORAGE_KEY, SORT_CONFIG } from "./constants";
import AzureIntegrationsEmpty from "./Empty";
import { useFilteredIntegrations } from "./useFilteredEntities";
import useSearchAzureIntegrations from "./useSearchCloudIntegrations";

const AzureIntegrationsList = () => {
  useSelfHostedEnabledCloudIntegrations();
  const trackAnalytics = useAnalytics();

  const { isPageLoading, error, integrations } = useSearchAzureIntegrations();
  const { integration } = useOutletContext<{ integration: IntegrationView }>();

  useLayoutHeading(integration.name);

  useBreadcrumbs([
    {
      title: "Integrations",
      link: "/integrations",
    },
    {
      title: integration.name,
    },
  ]);

  useLayoutActions(<CreateIntegrationButton />);

  const [searchParams] = useSearchParams();

  const isSearchActive = !!getSearchQuery(searchParams);

  const { sortDescriptor } = useTableSort({
    filtersOrderSettingsKey: AZURE_TABLE_FILTERS_STORAGE_KEY,
    sortConfig: SORT_CONFIG,
    initialSortOption: SORT_CONFIG[AZURE_COLUMN_ID.NAME],
    initialSortDirection: SearchQueryOrderDirection.Asc,
  });

  const filteredIntegrations = useFilteredIntegrations(integrations, sortDescriptor);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  return (
    <TableSimple
      data={filteredIntegrations}
      loading={isPageLoading}
      store={useIntegrationsAzureTableStore}
      ariaLabel="Azure integrations list"
      trackAnalytics={trackAnalytics}
      cellPerColumn={INTEGRATIONS_AZURE_TABLE_CELLS}
      filtersStorageKey={AZURE_TABLE_FILTERS_STORAGE_KEY}
      sortConfig={SORT_CONFIG}
      hasPagination={false}
      emptyState={
        isSearchActive ? (
          <EmptyState title="No results" icon={EmptystateSearchNoResultsColored} announce />
        ) : (
          <AzureIntegrationsEmpty />
        )
      }
    />
  );
};

export default AzureIntegrationsList;
